import { useState, useCallback } from "react";
import {
  Users,
  Lightbulb,
  Send,
  Rocket,
  Target,
  Sparkles,
  Hash,
  AlertCircle,
  Check,
} from "lucide-react";
import ContentStrategyService from "../../services/ContentStrategy";
import { displayToastMessage } from "../../utils/Toasts";
import { logAndFormatFormError } from "../../utils/Errors";
import config from "../../config";
import SubscriptionService from "../../services/Subscription";
import { Mixpanel } from "../../utils/Analytics";

// const initialFormState = {
//   basicInfo: {
//     name: "",
//     niche: "",
//     platforms: [],
//     accountAge: 0,
//     currentFollowers: 0,
//   },
//   contentStrategy: {
//     coreMessage: "",
//     contentFormats: [],
//     uniqueSellingPoint: "",
//     targetAudience: "",
//   },
//   growthGoals: {
//     followersTarget: "",
//     engagementGoals: "",
//     monetizationPlans: [],
//   },
//   marketingResources: {
//     weeklyContentCapacity: 3,
//     monthlyBudget: "$0-$100",
//     toolsAvailable: [],
//   },
// };

const getSavedFormData = () => {
  const savedData = localStorage.getItem("lastSuccessfulFormSubmission");
  if (savedData) {
    try {
      return JSON.parse(savedData);
    } catch (error) {
      console.error("Error parsing saved form data:", error);
      return null;
    }
  }
  return null;
};

// Then replace the current initialFormState const declaration with:
const initialFormState = getSavedFormData() || {
  basicInfo: {
    name: "EXAMPLE: FitWithEmma",
    niche: "Fitness & Wellness",
    platforms: ["TikTok", "Instagram Reels"],
    accountAge: 8,
    currentFollowers: 12500,
  },
  contentStrategy: {
    coreMessage:
      "Achievable fitness for busy professionals with simple home workouts and nutrition tips that fit into your schedule.",
    contentFormats: ["Tutorials", "Day in the Life", "Challenges", "Q&A"],
    uniqueSellingPoint:
      "15-minute workouts designed specifically for office workers with minimal equipment",
    targetAudience:
      "25-40 year old professionals who want to stay fit but struggle with time constraints and gym accessibility. Primarily urban dwellers in professional careers.",
  },
  growthGoals: {
    followersTarget: "50K",
    engagementGoals: "7% engagement rate with 1000+ comments per week",
    monetizationPlans: [
      "Brand Partnerships",
      "Affiliate Marketing",
      "Product Sales",
    ],
  },
  marketingResources: {
    weeklyContentCapacity: 5,
    monthlyBudget: "$200-$500",
    toolsAvailable: [
      "Smartphone Camera",
      "Basic Editing App",
      "Ring Light",
      "Tripod",
      "Canva",
    ],
  },
};

const platformOptions = ["TikTok", "Instagram Reels", "YouTube Shorts"];
const contentFormatOptions = [
  "Tutorials",
  "Behind-the-Scenes",
  "Product Showcases",
  "Challenges",
  "Trending Sounds",
  "Q&A",
  "Day in the Life",
];
const monetizationOptions = [
  "Brand Partnerships",
  "Affiliate Marketing",
  "Product Sales",
  "Platform Monetization",
  "Tips/Donations",
];
const toolOptions = [
  "Smartphone Camera",
  "DSLR/Mirrorless Camera",
  "Basic Editing App",
  "Professional Editing Software",
  "Ring Light",
  "Microphone",
  "Canva",
  "Tripod",
];

// UI Components
const Section = ({
  activeSection,
  setActiveSection,
  id,
  title,
  children,
  icon,
}: any) => (
  <div
    className={`mb-6 bg-white rounded-xl p-4 md:p-6 shadow-md cursor-pointer ${
      activeSection === id ? "border-2 border-indigo-400" : ""
    }`}
    onClick={(e) => {
      // Prevent propagation to child elements
      e.stopPropagation();
      setActiveSection(id);
    }}
  >
    <div className="flex items-center mb-4 text-indigo-600">
      {icon}
      <h2 className="text-xl font-bold ml-2">{title}</h2>
    </div>
    {activeSection === id && children}
  </div>
);

const InputField = ({
  section,
  field,
  label,
  value,
  onChange,
  placeholder,
  error,
}: any) => (
  <div className="mb-4">
    <label className="block text-sm font-medium mb-1 text-gray-700">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(section, field, e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-2 rounded-lg border ${
        error ? "border-red-500 bg-red-50" : "border-gray-300"
      } focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition`}
    />
    {error && (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);

const NumberField = ({
  section,
  field,
  label,
  value,
  onChange,
  placeholder,
  error,
}: any) => (
  <div className="mb-4">
    <label className="block text-sm font-medium mb-1 text-gray-700">
      {label}
    </label>
    <input
      type="number"
      min="0"
      value={value}
      onChange={(e) => onChange(section, field, e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-2 rounded-lg border ${
        error ? "border-red-500 bg-red-50" : "border-gray-300"
      } focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition`}
    />
    {error && (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);

const TextareaField = ({
  section,
  field,
  label,
  value,
  onChange,
  placeholder,
  error,
}: any) => (
  <div className="mb-4">
    <label className="block text-sm font-medium mb-1 text-gray-700">
      {label}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(section, field, e.target.value)}
      placeholder={placeholder}
      rows={3}
      className={`w-full px-4 py-2 rounded-lg border ${
        error ? "border-red-500 bg-red-50" : "border-gray-300"
      } focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition`}
    />
    {error && (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);

const CheckboxGroup = ({
  section,
  field,
  label,
  options,
  selected,
  onChange,
  error,
}: any) => (
  <div className="mb-4">
    <label className="block text-sm font-medium mb-2 text-gray-700">
      {label}
    </label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((option: any) => (
        <div key={option} className="flex items-center">
          <input
            type="checkbox"
            id={`${section}-${field}-${option}`}
            checked={selected.includes(option)}
            onChange={() => onChange(section, field, option)}
            className={`h-4 w-4 ${
              error ? "text-red-600 border-red-500" : "text-indigo-600"
            } focus:ring-indigo-500 rounded`}
          />
          <label
            htmlFor={`${section}-${field}-${option}`}
            className="ml-2 text-sm text-gray-700"
          >
            {option}
          </label>
        </div>
      ))}
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);

const TargetAudienceForm = ({ userId }: any) => {
  const [formData, setFormData] = useState<any>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState("basicInfo");
  const [errors, setErrors] = useState<any>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState<any>(null);

  // Handle text input changes
  const handleTextChange = (section: any, field: any, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Handle checkbox changes
  const handleCheckboxChange = (section: any, field: any, option: any) => {
    setFormData((prev: any) => {
      const currentOptions = prev[section][field];
      const updatedOptions = currentOptions.includes(option)
        ? currentOptions.filter((item: any) => item !== option)
        : [...currentOptions, option];

      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: updatedOptions,
        },
      };
    });
  };

  // Handle number input changes
  const handleNumberChange = (section: any, field: any, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: parseInt(value, 10) || 0,
      },
    }));
  };

  // Validate the form before submission
  const validateForm = useCallback(() => {
    const newErrors: any = {};

    // Validate Basic Info (all required per schema)
    if (!formData.basicInfo.name) {
      newErrors.name = "Name is required";
    }

    if (!formData.basicInfo.niche) {
      newErrors.niche = "Content niche is required";
    }

    if (formData.basicInfo.platforms.length === 0) {
      newErrors.platforms = "Select at least one platform";
    }

    if (formData.basicInfo.accountAge < 0) {
      newErrors.accountAge = "Account age cannot be negative";
    }

    if (formData.basicInfo.currentFollowers < 0) {
      newErrors.currentFollowers = "Followers cannot be negative";
    }

    // Validate Content Strategy
    if (!formData.contentStrategy.coreMessage) {
      newErrors.coreMessage = "Core message is required";
    }

    if (formData.contentStrategy.contentFormats.length === 0) {
      newErrors.contentFormats = "Select at least one content format";
    }

    // Validate Marketing Resources (required per schema)
    if (formData.marketingResources.weeklyContentCapacity < 0) {
      newErrors.weeklyContentCapacity = "Capacity cannot be negative";
    }

    if (!formData.marketingResources.monthlyBudget) {
      newErrors.monthlyBudget = "Monthly budget is required";
    }

    // Return errors object and whether the form is valid
    return { errors: newErrors, isValid: Object.keys(newErrors).length === 0 };
  }, [formData]);

  // Form submission function
  const handleSubmit = useCallback(
    async (e: any, userId: string | any) => {
      e.preventDefault();

      // Validate form
      const { errors: validationErrors, isValid } = validateForm();
      if (!isValid) {
        setErrors(validationErrors);

        // Set active section to the first section with errors
        if (
          validationErrors.name ||
          validationErrors.niche ||
          validationErrors.platforms ||
          validationErrors.accountAge ||
          validationErrors.currentFollowers
        ) {
          setActiveSection("basicInfo");
        } else if (
          validationErrors.coreMessage ||
          validationErrors.contentFormats
        ) {
          setActiveSection("contentStrategy");
        } else if (
          validationErrors.weeklyContentCapacity ||
          validationErrors.monthlyBudget
        ) {
          setActiveSection("marketingResources");
        }

        return;
      }

      setSubmitting(true);
      setErrors({});

      displayToastMessage(
        "info",
        "Generating your strategy...may take a few seconds/minutes"
      );
      displayToastMessage(
        "info",
        "Stay here or we will email you the strategy once it's ready!"
      );

      try {
        const contentStrategyService = new ContentStrategyService();
        const resBody: any = await contentStrategyService.generateStrategy({
          userId,
          formData,
        });
        if (resBody.error) {
          console.error("Error generating strategy:", resBody.error);
          throw new Error(resBody.error);
        }

        // Store upgradeInfo if it exists
        if (resBody.upgradeInfo) {
          setUpgradeInfo(resBody.upgradeInfo);
        }

        try {
          localStorage.setItem(
            "lastSuccessfulFormSubmission",
            JSON.stringify(formData)
          );
        } catch (error) {
          console.error("Error saving form data to localStorage:", error);
          // Continue execution even if saving to localStorage fails
        }

        setSubmitting(false);
        setFormSubmitted(true);
        displayToastMessage(
          "success",
          "Strategy generated successfully! Check your email."
        );
      } catch (error: any) {
        const errorMessage = logAndFormatFormError(
          "TargetAudienceForm.handleSubmit",
          error,
          "Generating content strategy"
        );
        displayToastMessage("error", errorMessage);
        setSubmitting(false);
        return;
      }
    },
    [validateForm, formData]
  );

  const handleUpgradeSubscriptionPlan = useCallback(
    async (e: any, subscriptionTier: string | any) => {
      try {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});

        displayToastMessage("info", "Upgrading subscription...please wait");

        const subscriptionService = new SubscriptionService();

        await subscriptionService.joinOrUpdateSubscription({
          userId,
          subscriptionPlan: `${subscriptionTier}Plan`,
        });

        displayToastMessage(
          "success",
          "Upgraded successfully! Please generate a new report to see the new results."
        );
        setSubmitting(false);
        setUpgradeInfo(null);
      } catch (error: any) {
        const errorMessage = logAndFormatFormError(
          "TargetAudienceForm.handleUpgradeSubscriptionPlan",
          error,
          "Upgrading subscription"
        );
        displayToastMessage("error", errorMessage);
        setSubmitting(false);
        return;
      }
    },
    [userId]
  );

  // Success screen after form submission
  if (formSubmitted) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl">
        <div className="text-center py-8">
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <Rocket className="h-10 w-10 text-green-600" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
            Your Strategy is Being Created!
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-lg mx-auto">
            Our AI is analyzing your inputs and crafting a personalized content
            strategy for your creator journey.
          </p>

          <div className="bg-white p-6 rounded-xl shadow-md max-w-lg mx-auto mb-8">
            <div className="flex items-center mb-4">
              <Target className="h-6 w-6 text-indigo-600 mr-2" />
              <h3 className="text-lg font-semibold">What's Happening Now</h3>
            </div>
            <ul className="space-y-4 text-left">
              <li className="flex items-start">
                <div className="bg-indigo-100 rounded-full p-1 mr-3 mt-0.5">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                </div>
                <p className="text-gray-700">
                  Analyzing trending content in the {formData.basicInfo.niche}{" "}
                  niche
                </p>
              </li>
              <li className="flex items-start">
                <div className="bg-indigo-100 rounded-full p-1 mr-3 mt-0.5">
                  <Users className="h-4 w-4 text-indigo-600" />
                </div>
                <p className="text-gray-700">
                  Identifying your ideal target audience profiles
                </p>
              </li>
              <li className="flex items-start">
                <div className="bg-indigo-100 rounded-full p-1 mr-3 mt-0.5">
                  <Hash className="h-4 w-4 text-indigo-600" />
                </div>
                <p className="text-gray-700">
                  Finding high-performing hashtags for your content
                </p>
              </li>
            </ul>
          </div>

          {/* New Upgrade Info Section */}
          {upgradeInfo && (
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-xl shadow-lg max-w-lg mx-auto mb-8 text-white">
              <div className="flex items-center mb-4">
                <Sparkles className="h-6 w-6 mr-2" />
                <h3 className="text-xl font-bold">Unlock More Features</h3>
              </div>

              <p className="mb-4">
                You're currently on our{" "}
                <span className="font-bold uppercase">
                  {upgradeInfo.currentTier}
                </span>{" "}
                plan. Upgrade to{" "}
                <span className="font-bold uppercase">
                  {upgradeInfo.nextTier}
                </span>{" "}
                for:
              </p>

              <ul className="space-y-2 mb-6">
                {upgradeInfo.additionalFeatures.map(
                  (feature: string, index: number) => (
                    <li key={index} className="flex items-start">
                      <div className="bg-white bg-opacity-20 rounded-full p-1 mr-3 mt-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                      <p>{feature}</p>
                    </li>
                  )
                )}
              </ul>

              <button
                onClick={(e) =>
                  handleUpgradeSubscriptionPlan(e, upgradeInfo.nextTier)
                }
                className="block w-full py-3 px-4 bg-white text-indigo-700 rounded-lg font-bold text-center hover:bg-opacity-90 transition-all shadow-md"
              >
                Upgrade Now
              </button>
            </div>
          )}

          <p className="text-gray-600 mb-8">
            Your strategy will be sent to your email soon. While you wait, check
            out our blog for creator tips!
          </p>
          <a
            href={config.links.blog}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-full shadow-md hover:shadow-lg transition-all"
          >
            Explore Creator Resources
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl">
      {/* Header Section */}
      <div className="text-center mb-6">
        <h1 className="text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-3">
          Define Your Target Audience
        </h1>
        <p className="text-gray-700 max-w-2xl mx-auto mb-4">
          Complete this form to get your personalized content strategy for
          short-form video growth
        </p>
        <p className="text-gray-700 max-w-2xl mx-auto mb-4">
          Want a dashboard for past reports?{" "}
          <a
            className="text-blue-600 underline"
            href="#request"
            onClick={(e: any) => {
              e.preventDefault();
              try {
                (window as any).gtag.track("event", "dashboard_request", {
                  userId,
                });
                Mixpanel.track("dashboard_request", { userId });
              } catch (error: any) {
                console.log("gTag or Mixpanel error:", error);
              }
              displayToastMessage("success", "Request recorded!");
            }}
          >
            Click here
          </a>{" "}
          so we hear you!
        </p>
      </div>

      {/* Form */}
      <form onSubmit={(e) => handleSubmit(e, userId)}>
        {/* Section Navigation for Mobile */}
        <div className="flex overflow-x-auto pb-2 mb-4 md:hidden space-x-2">
          <button
            type="button"
            onClick={() => setActiveSection("basicInfo")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "basicInfo"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Basic Info
          </button>
          <button
            type="button"
            onClick={() => setActiveSection("contentStrategy")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "contentStrategy"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Content Strategy
          </button>
          <button
            type="button"
            onClick={() => setActiveSection("growthGoals")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "growthGoals"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Growth Goals
          </button>
          <button
            type="button"
            onClick={() => setActiveSection("marketingResources")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "marketingResources"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Resources
          </button>
        </div>

        <Section
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          id="basicInfo"
          title="Basic Info"
          icon={<Users className="h-5 w-5" />}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <InputField
              section="basicInfo"
              field="name"
              label="Brand/Creator Name"
              value={formData.basicInfo.name}
              onChange={handleTextChange}
              placeholder="Your brand or creator name"
              error={errors.name}
            />
            <InputField
              section="basicInfo"
              field="niche"
              label="Content Niche"
              value={formData.basicInfo.niche}
              onChange={handleTextChange}
              placeholder="e.g. Beauty, Fitness, Comedy, Tech, Finance"
              error={errors.niche}
            />
            <CheckboxGroup
              section="basicInfo"
              field="platforms"
              label="Platforms You Use"
              options={platformOptions}
              selected={formData.basicInfo.platforms}
              onChange={handleCheckboxChange}
              error={errors.platforms}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NumberField
                section="basicInfo"
                field="accountAge"
                label="Account Age (months)"
                value={formData.basicInfo.accountAge}
                onChange={handleNumberChange}
                placeholder="0"
                error={errors.accountAge}
              />
              <NumberField
                section="basicInfo"
                field="currentFollowers"
                label="Current Followers"
                value={formData.basicInfo.currentFollowers}
                onChange={handleNumberChange}
                placeholder="0"
                error={errors.currentFollowers}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSection("contentStrategy");
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Next: Content Strategy
              </button>
            </div>
          </div>
        </Section>

        <Section
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          id="contentStrategy"
          title="Content Strategy"
          icon={<Lightbulb className="h-5 w-5" />}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <TextareaField
              section="contentStrategy"
              field="coreMessage"
              label="Core Message"
              value={formData.contentStrategy.coreMessage}
              onChange={handleTextChange}
              placeholder="What your content helps viewers accomplish or experience"
              error={errors.coreMessage}
            />
            <CheckboxGroup
              section="contentStrategy"
              field="contentFormats"
              label="Content Formats"
              options={contentFormatOptions}
              selected={formData.contentStrategy.contentFormats}
              onChange={handleCheckboxChange}
              error={errors.contentFormats}
            />
            <InputField
              section="contentStrategy"
              field="uniqueSellingPoint"
              label="Unique Selling Point"
              value={formData.contentStrategy.uniqueSellingPoint}
              onChange={handleTextChange}
              placeholder="What makes your content different from competitors"
            />
            <TextareaField
              section="contentStrategy"
              field="targetAudience"
              label="Target Audience Description"
              value={formData.contentStrategy.targetAudience}
              onChange={handleTextChange}
              placeholder="Describe who you're trying to reach (age, interests, problems they face)"
            />
            <div className="mt-4 flex justify-between">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSection("basicInfo");
                }}
                className="px-4 py-2 text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSection("growthGoals");
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Next: Growth Goals
              </button>
            </div>
          </div>
        </Section>

        <Section
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          id="growthGoals"
          title="Growth Goals"
          icon={<Rocket className="h-5 w-5" />}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <InputField
              section="growthGoals"
              field="followersTarget"
              label="3-Month Followers Target"
              value={formData.growthGoals.followersTarget}
              onChange={handleTextChange}
              placeholder="e.g. 10K, 100K"
            />
            <InputField
              section="growthGoals"
              field="engagementGoals"
              label="Engagement Goals"
              value={formData.growthGoals.engagementGoals}
              onChange={handleTextChange}
              placeholder="e.g. 5% engagement rate"
            />
            <CheckboxGroup
              section="growthGoals"
              field="monetizationPlans"
              label="Monetization Plans"
              options={monetizationOptions}
              selected={formData.growthGoals.monetizationPlans}
              onChange={handleCheckboxChange}
            />
            <div className="mt-4 flex justify-between">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSection("contentStrategy");
                }}
                className="px-4 py-2 text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSection("marketingResources");
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Next: Resources
              </button>
            </div>
          </div>
        </Section>

        <Section
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          id="marketingResources"
          title="Resources"
          icon={<Sparkles className="h-5 w-5" />}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NumberField
                section="marketingResources"
                field="weeklyContentCapacity"
                label="Weekly Content Capacity"
                value={formData.marketingResources.weeklyContentCapacity}
                onChange={handleNumberChange}
                placeholder="3"
                error={errors.weeklyContentCapacity}
              />
              <InputField
                section="marketingResources"
                field="monthlyBudget"
                label="Monthly Budget"
                value={formData.marketingResources.monthlyBudget}
                onChange={handleTextChange}
                placeholder="$0-$100"
                error={errors.monthlyBudget}
              />
            </div>
            <CheckboxGroup
              section="marketingResources"
              field="toolsAvailable"
              label="Tools Available"
              options={toolOptions}
              selected={formData.marketingResources.toolsAvailable}
              onChange={handleCheckboxChange}
            />
            <div className="mt-4 flex justify-between">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSection("growthGoals");
                }}
                className="px-4 py-2 text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </Section>

        {/* What To Expect Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 md:p-6 text-white mb-6 shadow-lg">
          <h2 className="text-lg md:text-xl font-bold mb-4 flex items-center">
            <Send className="h-5 w-5 mr-2" />
            What You'll Get After Submission
          </h2>
          <div className="space-y-3 text-sm md:text-base">
            <div className="flex">
              <Target className="h-5 w-5 mr-3 flex-shrink-0" />
              <p>
                Detailed target audience profile based on web research and
                platform trends
              </p>
            </div>
            <div className="flex">
              <Sparkles className="h-5 w-5 mr-3 flex-shrink-0" />
              <p>
                Video hook ideas, captivating captions, and creative concepts
                tailored to your audience
              </p>
            </div>
            <div className="flex">
              <Hash className="h-5 w-5 mr-3 flex-shrink-0" />
              <p>
                Relevant hashtags and keywords to research for partnership
                opportunities
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={submitting}
            className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 disabled:opacity-70"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Rocket className="mr-2 h-5 w-5" />
                Generate My Creator Strategy
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TargetAudienceForm;
